import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { standardGetHttpOptions, standardHttpOptions } from '@models';
import { LoadingService } from '@services/loading.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { ImageFavoriteInfo } from '@models/ImageFavorite/imageFavoriteInfo.model';
import { ImageFavoriteRequest } from '@models/ImageFavorite/imageFavoriteRequest.model';
import { ImageFavoriteImage } from '@models/ImageFavorite/imageFavoriteImage.model';
import { ImageLabelInfo } from '@models/ImageFavorite/imageLabelInfo.model';
import { ImageLabelRequest } from '@models/ImageFavorite/imageLabelRequest.model';
import { ImageLabelValue } from '@models/ImageFavorite/imageLabelValue.model';


@Injectable()
export class ImageFavoriteService {
  public standardHttpOptionsWithUser: any;

  private apiUrl: string;

  constructor(private http: HttpClient,
              private loadingService: LoadingService,
              @Inject(KIG_SETTINGS) private settings: KigSettings) {
    this.apiUrl = this.settings.apiUrl;
  }


  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }


  // ----- Image favorites -----

  public getImageFavorites(): Observable<ImageFavoriteInfo[]> {
    this.loadingService.setMessage('Loading image favorites ...');
    return this.http.get<ImageFavoriteInfo[]>(`${this.apiUrl}/imagefavorite`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  // Resolves to the new favorite's numeric id on success.
  public createImageFavorite(imageFavoriteRequest: ImageFavoriteRequest): Observable<any> {
    this.loadingService.setMessage('Creating image favorite ...');
    return this.http.post<number>(`${this.apiUrl}/imagefavorite`, imageFavoriteRequest, standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public updateImageFavorite(imageFavoriteRequest: ImageFavoriteRequest, username: string): Observable<any> {
    this.loadingService.setMessage('Updating image favorite ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${this.apiUrl}/imagefavorite`, imageFavoriteRequest, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public deleteImageFavorite(imageFavoriteId: number, username: string): Observable<any> {
    this.loadingService.setMessage('Deleting image favorite ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${this.apiUrl}/imagefavorite/${imageFavoriteId}`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  // Upload/replace the favorite's static background image.
  public uploadImage(imageFavoriteId: number, file: File, username: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    this.loadingService.setMessage('Uploading image...');
    return this.http.post(`${this.apiUrl}/imagefavorite/${imageFavoriteId}/uploadimage`, formData,
                          { withCredentials: true, headers: new HttpHeaders({ username }) }).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  // Static blob or newest gateway capture; null when no image is available.
  public getImage(imageFavoriteId: number): Observable<ImageFavoriteImage | null> {
    return this.http.get<ImageFavoriteImage>(`${this.apiUrl}/imagefavorite/${imageFavoriteId}/image`, standardGetHttpOptions).pipe(
      catchError(this.handleError));
  }


  public getLabelValues(imageFavoriteId: number): Observable<ImageLabelValue[]> {
    return this.http.get<ImageLabelValue[]>(`${this.apiUrl}/imagefavorite/${imageFavoriteId}/labelvalues`, standardGetHttpOptions).pipe(
      catchError(this.handleError));
  }


  // Newest image of a gateway (optionally with a media tag) for an unsaved
  // dynamic favorite (dashboard wizard preview); null when none is found.
  public getPreviewImage(gatewaySourceId: string, imageTag: string | null): Observable<ImageFavoriteImage | null> {
    return this.http.post<ImageFavoriteImage>(`${this.apiUrl}/imagefavorite/previewimage`,
                                              { gatewaySourceId, imageTag }, standardHttpOptions).pipe(
      catchError(this.handleError));
  }


  // Values for unsaved dynamic labels (dashboard wizard preview); ids may be temporary.
  public getPreviewLabelValues(labels: ImageLabelRequest[]): Observable<ImageLabelValue[]> {
    if (labels.length === 0) {
      return of([]);
    }
    return this.http.post<ImageLabelValue[]>(`${this.apiUrl}/imagefavorite/previewlabelvalues`, labels, standardHttpOptions).pipe(
      catchError(this.handleError));
  }


  // ----- Image labels -----

  public getImageLabels(imageFavoriteId: number): Observable<ImageLabelInfo[]> {
    return this.http.get<ImageLabelInfo[]>(`${this.apiUrl}/imagelabel/forfavorite/${imageFavoriteId}`, standardGetHttpOptions).pipe(
      catchError(this.handleError));
  }


  // Resolves to the new label's numeric id on success.
  public createImageLabel(imageLabelRequest: ImageLabelRequest): Observable<any> {
    return this.http.post<number>(`${this.apiUrl}/imagelabel`, imageLabelRequest, standardHttpOptions).pipe(
      catchError(this.handleError));
  }


  public updateImageLabel(imageLabelRequest: ImageLabelRequest, username: string): Observable<any> {
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${this.apiUrl}/imagelabel`, imageLabelRequest, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError));
  }


  public deleteImageLabel(imageLabelId: number, username: string): Observable<any> {
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${this.apiUrl}/imagelabel/${imageLabelId}`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError));
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }
}
